// src/components/CompetencesGridNeon.tsx
import React from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Chip,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

// Interface pour vos compétences
interface Competence {
    title: string;
    shortDesc: string;
    languages: string[];
    longDesc?: string;
}

interface GroupedSkill {
    title: {
        en: string;
        fr: string;
    };
    description: {
        en: string;
        fr: string;
    };
    skills: string[];
}

const groupedSkills: GroupedSkill[] = [
    {
        title: {
            en: "Programming Languages",
            fr: "Langages de programmation"
        },
        description: {
            en: "Proficiency in multiple programming languages for software development and solving complex problems.",
            fr: "Maîtrise de plusieurs langages pour le développement logiciel et la résolution de problèmes complexes."
        },
        skills: ["Java", "C++", "C#", "Python", "C", "TypeScript", "PHP"]
    },
    {
        title: {
            en: "Systems & Scripts",
            fr: "Systèmes et scripts"
        },
        description: {
            en: "Expertise in automating and managing systems using scripts and Unix environments.",
            fr: "Expertise dans l'automatisation et la gestion des systèmes via des scripts et environnements Unix."
        },
        skills: ["Bash", "Zsh"]
    },
    {
        title: {
            en: "Databases",
            fr: "Bases de données"
        },
        description: {
            en: "Design and management of robust, high-performance databases for scalable applications.",
            fr: "Conception et gestion de bases de données robustes et performantes pour des applications évolutives."
        },
        skills: ["MySQL", "PostgreSQL"]
    },
    {
        title: {
            en: "Frameworks & Technologies",
            fr: "Frameworks et technologies"
        },
        description: {
            en: "Utilization of frameworks to accelerate the development of modern, cross-platform applications.",
            fr: "Utilisation de frameworks pour accélérer le développement d'applications modernes et multi-plateformes."
        },
        skills: [".Net", "ReactNative", "EntityFramework", "MAUI"]
    },
    {
        title: {
            en: "Mobile Development",
            fr: "Développement mobile"
        },
        description: {
            en: "Creation of native and high-performance mobile applications.",
            fr: "Création d'applications mobiles natives et performantes."
        },
        skills: ["Android"]
    },
    {
        title: {
            en: "Management Tools",
            fr: "Outils de gestion"
        },
        description: {
            en: "Use of tools to plan, coordinate, and optimize project management.",
            fr: "Utilisation d'outils pour planifier, coordonner et optimiser la gestion de projets."
        },
        skills: ["MsProject"]
    },
    {
        title: {
            en: "Web Development",
            fr: "Développement web"
        },
        description: {
            en: "Design and creation of modern, interactive, and responsive websites.",
            fr: "Conception et réalisation de sites web modernes, interactifs et responsives."
        },
        skills: ["HTML", "CSS"]
    },
];

// Conteneur principal adapté au mode clair/sombre et responsive
const Container = styled(Box)(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : '#ffffff',
    color: theme.palette.text.primary,
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    borderRadius: theme.shape.borderRadius,
    maxWidth: 1200,
    margin: '40px auto',
}));

// Titre avec couleurs dynamiques et responsive
const AccentTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.8rem',
    },
}));

// Carte en forme ovale, avec styles adaptatifs
const OvalCard = styled(Card)(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : '#f7f7f7',
    color: theme.palette.text.primary,
    padding: theme.spacing(2),
    borderRadius: '50px',
    boxShadow:
        theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(255,255,255,0.1)'
            : '0 2px 8px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'transform 0.3s ease',
    '&:hover': {
        transform: 'scale(1.03)',
    },
}));

// Style commun aux chips (fixe)
const chipStyle = {
    backgroundColor: '#FF6200',
    color: '#fff',
    borderRadius: '9999px',
    fontWeight: 'bold',
    textTransform: 'none',
    padding: '0 12px',
    height: '28px',
    transition: 'transform 0.3s ease',
    '&:hover': { transform: 'scale(1.05)' },
};

interface CompetencesGridNeonProps {
    language: 'en' | 'fr';
}

export default function CompetencesGridNeon({ language }: CompetencesGridNeonProps) {
    const theme = useTheme();

    // Texte pour le titre de section
    const texts = {
        en: { title: "My Skills" },
        fr: { title: "Mes Compétences" },
    };

    return (
        <Container>
            <AccentTitle variant="h4">{texts[language].title}</AccentTitle>
            <Grid container spacing={3}>
                {groupedSkills.map((group, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <OvalCard>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {group.title[language]}
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                    {group.description[language]}
                                </Typography>
                                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {group.skills.map((skill, idx) => (
                                        <Chip key={idx} label={skill} sx={chipStyle} />
                                    ))}
                                </Box>
                            </CardContent>
                        </OvalCard>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
}
